export type EventHandler = (payload: any) => void;

// The server holds each poll for at most 15s; anything past 20s means the
// response stalled in a proxy and the poll must be re-issued.
const POLL_TIMEOUT_MS = 20000;
// How many consecutive hard failures (401/404) are tolerated before declaring
// the signaling session dead. Transient proxy/bot-challenge errors (403/429/5xx)
// never count towards this — they retry forever with backoff.
const MAX_HARD_FAILURES = 3;
// Signaling POSTs are fire-and-forget from the caller's perspective, but a
// silently lost offer/answer permanently breaks a peer pair, so transport-level
// events are retried. User-visible content events are single-shot: a retry can
// duplicate a message the server already processed (lost response, not lost
// request).
const SEND_ATTEMPTS = 3;
const NON_RETRYABLE_EVENTS = new Set([
  'chat_message', 'reaction', 'poll_created', 'poll_vote', 'poll_closed',
  'form_create', 'form_submit', 'form_close', 'notes_update',
  'live_captions', 'whiteboard_draw', 'recording_status',
]);

export class SignalingClient {
  private ws: WebSocket | null = null;
  private token: string = '';
  private roomId: string = '';
  private handlers: Map<string, EventHandler[]> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 6;
  private isIntentionallyClosed = false;
  private isHttpFallback = false;
  private fallbackTimer: any = null;
  private abortCtrl: AbortController | null = null;
  private currentPollCtrl: AbortController | null = null;

  public onOpen?: () => void;
  public onClose?: () => void;
  public onError?: (err: Event) => void;
  public onReconnecting?: (attempt: number, delayMs: number) => void;
  public onReconnected?: () => void;

  public isConnected(): boolean {
    return (this.ws !== null && this.ws.readyState === WebSocket.OPEN) || this.isHttpFallback;
  }

  public getTransportType(): 'websocket' | 'http-polling' {
    return this.isHttpFallback ? 'http-polling' : 'websocket';
  }

  public connect(token: string, roomId?: string) {
    if (this.isConnected()) {
      return;
    }
    this.token = token;
    this.isIntentionallyClosed = false;

    // Extract room ID
    if (roomId) {
      this.roomId = roomId;
    } else {
      this.roomId = this.extractRoomIdFromToken(token);
      if (!this.roomId) {
        const m = window.location.pathname.match(/\/room\/([^\/\?#]+)/);
        if (m) this.roomId = m[1];
      }
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const url = `${protocol}//${host}/ws?token=${encodeURIComponent(token)}`;

    // Set aggressive fallback timer: if WebSocket handshake does not complete in 1.8s (e.g. reverse proxy limitation), fallback to HTTP polling
    clearTimeout(this.fallbackTimer);
    this.fallbackTimer = setTimeout(() => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        console.info('[Signaling] WebSocket connection pending or restricted by reverse proxy, engaging high-speed HTTP polling transport');
        this.activateHttpFallback();
      }
    }, 1800);

    try {
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        // The handshake may complete just after the fallback timer fired — the
        // socket is already abandoned and polling owns the session.
        if (this.isHttpFallback || this.isIntentionallyClosed) {
          try { this.ws?.close(); } catch (_) {}
          return;
        }
        clearTimeout(this.fallbackTimer);
        const wasReconnecting = this.reconnectAttempts > 0;
        this.reconnectAttempts = 0;
        if (this.onOpen) this.onOpen();
        if (wasReconnecting && this.onReconnected) this.onReconnected();
      };

      this.ws.onmessage = (event) => {
        try {
          const lines = event.data.split('\n');
          for (const line of lines) {
            if (!line.trim()) continue;
            const msg = JSON.parse(line);
            this.dispatchEvent(msg.event, msg.payload);
          }
        } catch (err) {
          console.warn('Failed to parse WebSocket message:', err);
        }
      };

      this.ws.onclose = () => {
        if (!this.isHttpFallback && !this.isIntentionallyClosed) {
          if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 10000);
            if (this.onReconnecting) this.onReconnecting(this.reconnectAttempts, delay);
            setTimeout(() => this.connect(this.token, this.roomId), delay);
          } else {
            this.activateHttpFallback();
          }
        }
      };

      this.ws.onerror = (_err) => {
        if (!this.isHttpFallback && !this.isConnected()) {
          this.activateHttpFallback();
        }
      };
    } catch (_) {
      this.activateHttpFallback();
    }
  }

  private extractRoomIdFromToken(token: string): string {
    try {
      const parts = token.split('.');
      if (parts.length >= 2) {
        // Server tokens are base64url (RawURLEncoding) — normalize before atob
        const b64 = parts[0].replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(b64));
        return payload.rid || payload.room_id || '';
      }
    } catch (_) {}
    return '';
  }

  private async activateHttpFallback() {
    if (this.isHttpFallback || this.isIntentionallyClosed) return;
    this.isHttpFallback = true;
    clearTimeout(this.fallbackTimer);

    if (this.ws) {
      try { this.ws.close(); } catch (_) {}
      this.ws = null;
    }

    if (this.onOpen) this.onOpen();

    this.abortCtrl = new AbortController();
    this.runHttpPollLoop();
  }

  private async runHttpPollLoop() {
    // AbortController scoped per request: never leave a poll in flight without
    // a watchdog, or a stalled proxy response freezes inbound signaling forever
    // while the UI still shows "connected".
    let hardFailures = 0;

    while (!this.isIntentionallyClosed && this.isHttpFallback) {
      const pollCtrl = new AbortController();
      this.currentPollCtrl = pollCtrl;
      let timedOut = false;
      const timer = setTimeout(() => {
        timedOut = true;
        pollCtrl.abort();
      }, POLL_TIMEOUT_MS);

      try {
        const url = `/api/v1/meetings/${this.roomId}/poll?token=${encodeURIComponent(this.token)}`;
        const res = await fetch(url, {
          signal: pollCtrl.signal,
          headers: { 'Cache-Control': 'no-cache' }
        });

        if (res.ok) {
          hardFailures = 0;
          const list = await res.json();
          clearTimeout(timer);
          if (Array.isArray(list)) {
            for (const msg of list) {
              if (msg && msg.event) {
                this.dispatchEvent(msg.event, msg.payload);
              }
            }
          }
        } else if (res.status === 401 || res.status === 404) {
          clearTimeout(timer);
          // Definitive auth/meeting-gone error — retry briefly in case an
          // intermediary faked the status, then declare the session dead.
          hardFailures++;
          if (hardFailures >= MAX_HARD_FAILURES) {
            console.warn('[Signaling] HTTP polling unauthorized or meeting ended');
            if (this.onClose) this.onClose();
            break;
          }
          await new Promise(r => setTimeout(r, 800));
        } else {
          clearTimeout(timer);
          // The server's own permanent 403 (kicked/banned/room denied) carries
          // X-Verve-Poll-Error; an intermediary's 403 (bot challenge) does not.
          const pollError = res.headers.get('x-verve-poll-error');
          if (pollError) {
            console.warn(`[Signaling] Poll rejected by server (${pollError}) — stopping signaling`);
            if (this.onClose) this.onClose();
            break;
          }
          // 403 bot-challenge, 429 rate limit, 5xx — transient edge trouble.
          // Keep retrying with backoff; never kill signaling over these.
          hardFailures = 0;
          await new Promise(r => setTimeout(r, 1200));
        }
      } catch (err: any) {
        clearTimeout(timer);
        if (this.isIntentionallyClosed) break;
        if (timedOut) {
          console.warn('[Signaling] Poll response stalled past watchdog, re-issuing poll');
          await new Promise(r => setTimeout(r, 200));
        } else {
          await new Promise(r => setTimeout(r, 1200));
        }
      }
    }
  }

  public reconnectNow() {
    this.close();
    this.isIntentionallyClosed = false;
    this.connect(this.token, this.roomId);
  }

  public send(event: string, payload?: any) {
    const frame = {
      version: '1.0',
      type: 'event',
      event: event,
      payload: payload || {},
      timestamp: Math.floor(Date.now() / 1000),
    };

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(frame));
      return;
    }

    // HTTP Dispatch fallback with retry: a silently lost peer_offer/answer
    // permanently breaks that peer pair (offers are not re-sent by the app
    // layer except via the negotiation watchdog, so best not to lose them).
    if (this.roomId) {
      const attempts = NON_RETRYABLE_EVENTS.has(event) ? 1 : SEND_ATTEMPTS;
      const postSignal = async (): Promise<void> => {
        let lastErr: any = null;
        for (let attempt = 1; attempt <= attempts; attempt++) {
          try {
            const res = await fetch(`/api/v1/meetings/${this.roomId}/signal?token=${encodeURIComponent(this.token)}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(frame),
            });
            if (res.ok) return;
            if (res.status >= 500 || res.status === 429) {
              lastErr = new Error(`signal POST ${event} -> ${res.status}`);
            } else {
              // 401/403/404: retrying won't help
              console.warn(`[Signaling] Failed to send signal ${event} over HTTP:`, res.status);
              return;
            }
          } catch (err: any) {
            lastErr = err;
          }
          if (attempt < attempts) {
            await new Promise(r => setTimeout(r, 250 * attempt));
          }
        }
        if (lastErr) {
          console.warn(`[Signaling] Failed to send signal ${event} over HTTP:`, lastErr);
        }
      };
      postSignal();
    }
  }

  public on(event: string, handler: EventHandler) {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, []);
    }
    const list = this.handlers.get(event)!;
    // Idempotent registration: MeetingRoom re-runs webrtc.init()/connectSignaling()
    // on waiting-room admission, and duplicated peer_offer handlers would each
    // re-apply the same offer, rolling back stable connections.
    if (!list.includes(handler)) {
      list.push(handler);
    }
  }

  public off(event: string, handler: EventHandler) {
    const list = this.handlers.get(event);
    if (!list) return;
    this.handlers.set(event, list.filter(h => h !== handler));
  }

  private dispatchEvent(event: string, payload: any) {
    const list = this.handlers.get(event);
    if (list) {
      for (const h of list) {
        try {
          h(payload);
        } catch (err) {
          console.error(`Error in signaling handler for ${event}:`, err);
        }
      }
    }
  }

  public blockReconnect() {
    this.isIntentionallyClosed = true;
    this.reconnectAttempts = this.maxReconnectAttempts + 1;
    this.close();
  }

  public close() {
    this.isIntentionallyClosed = true;
    clearTimeout(this.fallbackTimer);
    if (this.abortCtrl) {
      this.abortCtrl.abort();
      this.abortCtrl = null;
    }
    // Abort the in-flight long-poll (if any) so closing a meeting does not
    // leave a parked request holding the participant's registration open.
    if (this.currentPollCtrl) {
      this.currentPollCtrl.abort();
      this.currentPollCtrl = null;
    }
    if (this.ws) {
      try { this.ws.close(); } catch (_) {}
      this.ws = null;
    }
    this.isHttpFallback = false;
  }
}
